import { useFetchUsedImageList, usePutImageUse } from '@api';
import { PathnameEnum, ToastMessageEnum } from '@constant';
import { useQueryClient } from '@tanstack/react-query';
import { IImageListItem, IImageModalType, ISelectedImageInfo, ISequenceInfo } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from './useToast';

export const useUsedImageList = () => {
  const [isImageModalVisible, setIsImageModalVisible] = useState<boolean>(false);
  const [imageModalType, setImageModalType] = useState<IImageModalType>({ needSequence: false, needSimilar: false });
  const [selectedImageInfo, setSelectedImageInfo] = useState<ISelectedImageInfo | null>(null);
  const [sequenceInfo, setSequenceInfo] = useState<ISequenceInfo | null>(null);

  const { showToast } = useToast();

  const navigate = useNavigate();
  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const queryClient = useQueryClient();

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchUsedImageList();

  const { mutateAsync: putImageUseMutateAsync } = usePutImageUse((err) => {
    // PUT 요청 실패 시 캐시 데이터 복구
    if (data) {
      const currentQueryKey = ['get', `/images/used-images`];

      // 실패한 요청의 imageId 추출
      const failedImageId = Number(err.config?.url?.split('/').pop());

      // 이전 상태로 데이터 복구
      const restoredPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === failedImageId) {
              return {
                ...item,
                isUsed: !item.isUsed, // 토글된 상태를 다시 원래대로
                usedCount: item.isUsed ? item.usedCount - 1 : item.usedCount + 1 // 변경된 usedCount를 다시 원래대로
              };
            }
            return item;
          })
        }
      }));

      // 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: restoredPages
      }));

      // 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none'
      });
    }
  });

  const handleBreadCrumbClick = () => {
    navigate(PathnameEnum.COLLECTION_LIST_PAGE);
  };

  const flattenUsedImageList = () => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: IImageListItem[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: IImageListItem) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }
      return flattenedArr;
    } else {
      return [];
    }
  };

  const handleImageClick = (
    imageId: number,
    s3Url: string,
    sequenceInfo: ISequenceInfo | null,
    isUsed?: boolean,
    usedCount?: number,
    isCollected?: boolean
  ) => {
    const isAdultsOnlyOutputPage = true; //현재 사용한 이미지로 설정할 수 있는 이미지는 19금 이미지 뿐이므로 무조건 true인 상황. 추후 필요시 로직 변경
    setImageModalType({ needSequence: Boolean(sequenceInfo), needSimilar: isAdultsOnlyOutputPage });
    setSelectedImageInfo({ id: imageId, s3Url: s3Url, isUsed: isUsed, usedCount: usedCount, isCollected: isCollected });
    setSequenceInfo(sequenceInfo);
    setIsImageModalVisible(true);
  };

  const handleImageModalClose = () => {
    setIsImageModalVisible(false);
  };

  const handleImageUseButtonClick = (imageId: number, isUsed: boolean, usedCount: number, isCollected?: boolean) => {
    if (data) {
      // 모달 내 isUsed 및 usedCount값 업데이트
      if (isImageModalVisible) {
        setSelectedImageInfo({
          id: selectedImageInfo?.id ?? 0,
          s3Url: selectedImageInfo?.s3Url ?? '',
          isUsed: !isUsed,
          usedCount: !isUsed ? usedCount + 1 : usedCount - 1,
          isCollected: Boolean(isCollected)
        });
      }

      // 1. useFetchImageList와 동일한 쿼리 키 사용
      const currentQueryKey = ['get', `/images/used-images`];

      // 2. 새로운 페이지 데이터 생성
      const newPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === imageId) {
              return {
                ...item,
                isUsed: !isUsed,
                usedCount: !isUsed ? usedCount + 1 : usedCount - 1
              };
            }
            return item;
          })
        }
      }));

      // 3. 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => {
        if (!oldData) {
          return {
            pageParams: [1], // 초기 페이지 파라미터 설정
            pages: newPages
          };
        }

        return {
          pageParams: oldData.pageParams,
          pages: newPages
        };
      });

      // 4. 쿼리 무효화하여 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none' // 실제 API 호출은 하지 않고 캐시만 업데이트
      });

      // 5. PUT /images/used-image/:id 요청
      putImageUseMutateAsync(imageId).then(() => {
        // 요청 성공시 토스트 메세지 보여주기
        showToast(isUsed ? ToastMessageEnum.CANCEL_IMAGE_USE : ToastMessageEnum.ADD_IMAGE_USE);
      });
    }
  };

  // 사용한 이미지 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  return {
    handleBreadCrumbClick,
    data,
    flattenUsedImageList,
    outputBottomRef,

    isImageModalVisible,
    imageModalType,
    selectedImageInfo,
    sequenceInfo,
    handleImageClick,
    handleImageModalClose,
    handleImageUseButtonClick
  };
};
